import * as Yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum K1893LineItemKeys {
  itemNumber = 'itemNumber',
  description = 'description',
  blendSpec = 'blendSpec',
  eaUPC = 'eaUPC',
  unitsPerPack = 'unitsPerPack',
  packSize = 'packSize',
  caseSize = 'caseSize',
  caseQtyOrdered = 'caseQtyOrdered',
  totalPacks = 'totalPacks',
  totalUnits = 'totalUnits',
  caseCost = 'caseCost',
  lineTotal = 'lineTotal',
}

const K1893LineItemSchema = Yup.object().shape({
  [K1893LineItemKeys.itemNumber]: Yup.number().nullable().optional(),
  [K1893LineItemKeys.description]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.blendSpec]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.eaUPC]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.unitsPerPack]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.packSize]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.caseSize]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.caseQtyOrdered]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.totalPacks]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.totalUnits]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.caseCost]: Yup.string().nullable().optional(),
  [K1893LineItemKeys.lineTotal]: Yup.string().nullable().optional(),
})

export const K1893PurchaseOrderFileDataSchema = Yup.object().shape({
  buyer: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      emailAddress: Yup.string().nullable().optional(),
    })
    .required(),
  vendor: Yup.object()
    .shape({
      companyName: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      phoneNumber: Yup.string().nullable().optional(),
      emailAddress: Yup.string().nullable().optional(),
    })
    .required(),
  purchaseOrderNumber: Yup.string().nullable().optional(),
  dateIssued: Yup.string().nullable().optional(),
  dateReady: Yup.string().nullable().optional(),
  paymentTerms: Yup.string().nullable().optional(),
  freightTerms: Yup.string().nullable().optional(),
  destination: Yup.object()
    .shape({
      company: Yup.string().nullable().optional(),
      addressLine1: Yup.string().nullable().optional(),
      contactName: Yup.string().nullable().optional(),
      contactEmail: Yup.string().nullable().optional(),
      contactPhone: Yup.string().nullable().optional(),
    })
    .required(),
  items: Yup.array().of(K1893LineItemSchema).required(),
  instructionsHeader: Yup.string().nullable().optional(),
  comments: Yup.string().nullable().optional(),
  vendorInstructions: Yup.string().nullable().optional(),
})

export type K1893PurchaseOrderFileData = Yup.InferType<typeof K1893PurchaseOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<K1893PurchaseOrderFileData>

export const k1893PurchaseOrderFileMappings: RequiredFileData = {
  buyer: {
    companyName: '48dd76bf-2146-4081-93b1-997e96564e18',
    addressLine1: 'bef0f9bb-2c1e-493e-aacd-a95f800c16c7',
    phoneNumber: '5096eb2d-2247-4e4b-bf0b-1cdade76a42c',
    emailAddress: 'afa0dca4-34e9-4107-a010-b53116060085',
  },
  vendor: {
    companyName: '0088ae82-2e27-4f3b-a658-da447e5d1eab',
    addressLine1: '9af7c715-2e75-48a9-89ae-7db1e27c1ec5',
    phoneNumber: 'afbde467-7fce-4f08-9bd0-dd0090f12025',
    emailAddress: 'b52212dc-43b6-4216-80b6-274f150e69ec',
  },
  purchaseOrderNumber: 'bee5a0c3-a53c-4e33-a181-9f6ac39f0aaf',
  paymentTerms: '7ef080f1-32bc-4e1c-9529-c45e018811ac',
  freightTerms: '3b162126-c935-4056-b1cd-8a910fd35c59',
  dateIssued: '7f7077d2-f752-48fd-a162-27a67730eacf',
  dateReady: '55d94604-a7b3-4178-a021-34d33025a42f',
  destination: {
    company: 'f0131a62-adbe-46be-b863-c9a16ba82744',
    addressLine1: '7b536ee3-d102-44fe-aed0-42f0f425a2a5',
    contactName: '8a20ab83-e3d0-450b-b941-dc0ca48cac7e',
    contactEmail: '98f1c9e3-b2bc-49e8-ad13-4dcaa10ef1a5',
    contactPhone: 'b57a132d-3c78-4635-88e6-3657b92bd370',
  },
  instructionsHeader: 'c1e3ae56-182f-4335-a770-d5890c300e65',
  comments: 'de3f98dd-deae-4428-95f4-9d7af746c4f5',
  vendorInstructions: '83aac6a4-0abe-4359-9e08-775d0bc0b28a',
  items: [
    {
      itemNumber: null,
      description: '11e787dc-8787-487c-861d-58396f63e72e',
      blendSpec: '0c9608bd-9eb4-4bd7-a0c4-e5b48e1c5ae2',
      eaUPC: '69956e20-e5c2-4f44-9126-f51397602d52',
      unitsPerPack: 'b19670fa-b3dd-4e48-875d-5106059cee9c',
      packSize: '7125670e-f04b-4037-931b-ae9ac917fa00',
      caseSize: 'a8c8f8b0-cd23-436f-9ba6-930a978f3737',
      caseQtyOrdered: 'a3d8e8ce-05a8-41e5-b5f8-c841f31f9407',
      totalPacks: null,
      totalUnits: '9d8e14ad-a94c-4c19-8438-e8b51cff337a',
      caseCost: '4d78e4aa-e1ca-42ba-83fc-fb010798d10b',
      lineTotal: null,
    },
  ],
}
